import { useMutation, useQueryClient } from '@tanstack/react-query'
import { accountAPI } from '@/api/client'
import { useToast } from '@/hooks/use-toast'
import type { AccountResponseDto, UpdateAccountDto } from '@/types/account'

interface MutationContext {
  previousAccount?: AccountResponseDto
}

/**
 * React Query mutation hook for updating user account data.
 *
 * Features:
 * - Optimistic updates for immediate UI feedback
 * - Automatic cache invalidation on success
 * - Rollback on error
 * - Toast notifications for success/error
 *
 * @returns Mutation object with mutate, mutateAsync, isLoading, error
 *
 * @example
 * ```tsx
 * const updateMutation = useUpdateAccount();
 *
 * const handleSave = async () => {
 *   try {
 *     await updateMutation.mutateAsync({
 *       firstName: 'John'
 *     });
 *     // Success handled automatically with toast
 *   } catch (error) {
 *     // Error handled automatically with toast
 *   }
 * };
 * ```
 */
export function useUpdateAccount() {
  const queryClient = useQueryClient()
  const { toast } = useToast()

  return useMutation<
    AccountResponseDto,
    Error,
    UpdateAccountDto,
    MutationContext
  >({
    mutationFn: accountAPI.updateAccount,

    // Optimistic update: Update cache immediately
    onMutate: async updatedData => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: ['account'] })

      // Snapshot the previous value
      const previousAccount = queryClient.getQueryData<AccountResponseDto>([
        'account',
      ])

      // Optimistically update to the new value
      if (previousAccount) {
        queryClient.setQueryData<AccountResponseDto>(['account'], {
          ...previousAccount,
          ...updatedData,
        })
      }

      // Return context with the snapshot
      return { previousAccount }
    },

    // On success: Show success toast and invalidate cache
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['account'] })
      toast({
        title: 'Account updated',
        description: 'Your account information has been saved successfully.',
      })
    },

    // On error: Rollback to previous value and show error toast
    onError: (error, _variables, context) => {
      // Rollback to the previous value
      if (context?.previousAccount) {
        queryClient.setQueryData(['account'], context.previousAccount)
      }

      // Handle specific error types
      if (error.message.includes('400')) {
        toast({
          title: 'Invalid data',
          description: 'Please check your inputs and try again.',
          variant: 'destructive',
        })
      } else {
        toast({
          title: 'Update failed',
          description:
            error.message || 'An unexpected error occurred. Please try again.',
          variant: 'destructive',
        })
      }
    },
  })
}
